import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Toyota Supra Mk4 (A80, ~1997) — the iconic JDM sports coupe at true scale
// (1 unit = 1 metre): length ~4.51, width 1.81, height ~1.24 (LOW), wheelbase
// 2.55 (front axle +1.10, rear -1.45), ~0.33 m wheels. Nose is +Z. Authored
// for the `cars` showcase batch.
//
// The silhouette is ONE LOW CONTINUOUS FASTBACK, built like the Porsche 911 in
// this batch: a long low bonnet flows up a hard-raked windscreen to a short low
// roof (the highest point), then the roof sweeps in one UNBROKEN descending
// line — backlight sail -> boot deck -> tail lip — down to a rounded tail. No
// flat deck, no box-on-top cabin: the rear bodywork and the roofline are the
// same falling surface (never a pickup). The rear haunches sit LOW, below the
// beltline, flaring gently wider than the front so the car reads planted and
// muscular. THE defining cue is the tall "basket handle" REAR WING: a wide flat
// black blade on two clearly TALL uprights, floating high above the boot deck
// with an obvious open gap beneath — the Mk4's signature, reading as a floating
// wing from all four views. The front is a smooth rounded body-colour nose with
// big teardrop headlamps swept into the front wings, a small lower intake and
// round fog lamps. Body panels carry the "paint" recolour slot (iconic Supra
// orange); `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#e0621a"; // iconic Supra bright orange — the "paint" slot default
const TRIM = "#141518"; // gloss black (wing, pillars, grille base)
const CLAD = "#202225"; // lower valances, sills, diffuser
const WELL = "#141518"; // wheel-well discs
const GLASS = "#161c22"; // near-black side / rear / hatch glazing
const SCREEN = "#1f2933"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const SPOKE = "#d3d8de"; // bright five-spoke alloys
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#b5152a"; // wrap tail lamps, deeper than the paint so they read
const REDDARK = "#6c1120";
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). Deliberately LOW and hunkered: belt line ~0.74
// (barely above the wheel tops), roof peak ~1.185, wide track so the tyre outer
// faces stay proud of the body. Every square view reads clearly wider than tall.
const WHEEL_R = 0.33;
const AXLE_F = 1.1;
const AXLE_R = -1.45; // wheelbase 2.55
const WHEEL_X_F = 0.82; // half track, front
const WHEEL_X_R = 0.84; // half track, rear — wider under the haunches
const TYRE_W_F = 0.24;
const TYRE_W_R = 0.27;

const SCREEN_RAKE = 0.888; // hard-raked curved windscreen (top toward the tail)
const HOOD_SLOPE = 0.08; // long bonnet lid diving gently to the low nose

// The unbroken fastback line behind the roof — backlight sail -> boot deck ->
// tail lip — as three tilted boxes whose top faces meet on shared breakpoints
// (z, y_top): (-0.42, 1.15) -> (-1.15, 0.86) -> (-1.85, 0.76) -> (-2.15, 0.72).
// Each box overlaps its neighbour so no seam opens; the whole thing is one low
// descending surface (a fastback, never a flat pickup deck).
const SAIL_ANGLE = 0.378; // steep rear-glass-hatch drop off the roof
const DECK_ANGLE = 0.142; // boot lid, the shallow fall onto the tail
const TAIL_ANGLE = 0.133; // final short tail lip

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the low muscular rear haunch shoulders and the softened tail so the Supra
 *  reads as a smooth rounded coupe, not a stack of blocks. Kept LOW (below the
 *  roofline) so it never humps up beside the greenhouse. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One five-spoke alloy over a low-profile tyre: tyre, brake disc, dark dish,
 *  five bright spokes proud of the tyre face, chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.205, 0.205, 0.06, 18, DISH, [side * (outer - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.055, 0.37, 0.085, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.028, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits in the bumper below the headlamps; rear pair is the amber
// outer end of each wrap tail lamp.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.66, 0.46, 2.28], size: [0.18, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.66, 0.46, 2.28], size: [0.18, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.78, 0.6, -2.22], size: [0.1, 0.1, 0.03] },
  { name: "indicator-rr", pos: [0.78, 0.6, -2.22], size: [0.1, 0.1, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear -----------------------------------------------------------
  P.push(
    ...wheel(-1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.55, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.86, 0.42, 0.4, 0.05)); // front arch
    P.push(...arch(s, AXLE_R, 0.88, 0.43, 0.41, 0.055)); // rear arch, a touch wider
  }
  P.push(box(1.6, 0.12, 3.9, TRIM, [0, 0.19, -0.05])); // underbody pan

  // -- Body masses (paint) — one low rounded body ----------------------------
  P.push(paint(1.8, 0.46, 4.0, [0, 0.53, 0.0])); // main tub, sill to belt line (y 0.30..0.76)
  P.push(paint(1.76, 0.1, 3.2, [0, 0.76, -0.3])); // shoulder band, cowl to tail (belt highlight)

  // Long low bonnet: a lid diving gently to the nose with the front wings
  // beside it (where the teardrop lamps sit). Kept flush with the belt so the
  // front reads as one smooth falling line, not a stacked block.
  P.push(paint(1.5, 0.09, 1.5, [0, 0.79, 1.25], [HOOD_SLOPE, 0, 0])); // bonnet lid
  for (const s of S) P.push(paint(0.22, 0.1, 1.5, [s * 0.79, 0.79, 1.25], [HOOD_SLOPE, 0, 0])); // front-wing tops
  P.push(paint(1.78, 0.42, 0.18, [0, 0.55, 2.22])); // smooth rounded nose / bumper face (y 0.34..0.76)
  P.push(paint(1.62, 0.1, 0.28, [0, 0.73, 2.02])); // nose top cap, blending bonnet front to nose face
  P.push(blob([0.86, 0.14, 0.16], [0, 0.66, 2.24])); // rounded nose crown (softens the front)
  P.push(box(1.5, 0.13, 0.3, CLAD, [0, 0.31, 2.2])); // front lower valance / splitter, back to the arch
  for (const s of S) P.push(box(0.05, 0.12, 2.1, CLAD, [s * 0.885, 0.34, -0.1])); // rocker sills, arch to arch

  // -- Low muscular rear haunches (flare wider than the front, kept BELOW the
  //    beltline so they never hump up beside the roof) -----------------------
  for (const s of S) {
    P.push(paint(0.14, 0.42, 1.5, [s * 0.86, 0.52, -1.25])); // haunch quarter panel, outer ~0.93
    P.push(blob([0.16, 0.12, 0.74], [s * 0.8, 0.66, -1.2])); // rounded low haunch shoulder (top ~0.78)
  }

  // -- The unbroken fastback: roof -> backlight sail -> boot deck -> tail. -----
  // Three tilted boxes overlapping edge-to-edge so the roof falls to the tail in
  // ONE low descending line with NO flat deck (the rear bodywork IS the roofline
  // continued — never a pickup). A small dark interior mass backs the glass.
  P.push(box(1.16, 0.24, 1.35, CABIN, [0, 0.94, -0.3])); // interior block, fully behind glass
  P.push(paint(1.34, 0.4, 0.86, [0, 0.805, -0.785], [-SAIL_ANGLE, 0, 0])); // backlight sail
  P.push(paint(1.5, 0.34, 0.82, [0, 0.64, -1.5], [-DECK_ANGLE, 0, 0])); // boot deck
  P.push(paint(1.6, 0.26, 0.5, [0, 0.61, -2.0], [-TAIL_ANGLE, 0, 0])); // final tail lip
  P.push(paint(1.7, 0.4, 0.13, [0, 0.55, -2.13])); // rounded tail panel face
  P.push(blob([0.82, 0.09, 0.11], [0, 0.72, -2.09])); // rounded tail top edge

  // The big fastback glass hatch lying on the sail (the Mk4's glass rear hatch).
  P.push(box(1.12, 0.03, 0.84, GLASS, [0, 1.025, -0.785], [-SAIL_ANGLE, 0, 0]));

  // -- Greenhouse (a LOW cabin; the roof is the single highest point) ---------
  P.push(box(1.3, 0.58, 0.045, SCREEN, [0, 0.935, 0.36], [-SCREEN_RAKE, 0, 0])); // curved windscreen
  for (const s of S) P.push(paint(0.09, 0.56, 0.09, [s * 0.6, 0.935, 0.36], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(paint(1.34, 0.07, 0.58, [0, 1.15, -0.13])); // roof, overlapping screen top + sail top
  for (const s of S) {
    P.push(box(0.03, 0.28, 1.05, GLASS, [s * 0.7, 0.95, -0.14], [0, 0, s * 0.05])); // side glass band
    P.push(box(0.05, 0.28, 0.07, TRIM, [s * 0.7, 0.95, 0.05], [0, 0, s * 0.05])); // B-pillar
    P.push(paint(0.06, 0.3, 0.36, [s * 0.7, 0.93, -0.55], [0, 0, s * 0.05])); // C-pillar (buttress into the sail)
  }

  // -- Basket-handle REAR WING: a wide flat black blade on two clearly TALL,
  //    slim uprights, floating HIGH above the boot deck with an obvious open
  //    gap beneath — the Mk4's signature, reading as a floating wing all round.
  for (const s of S) {
    P.push(box(0.06, 0.6, 0.08, TRIM, [s * 0.55, 1.11, -1.72])); // tall slim upright (y 0.81..1.41)
  }
  P.push(box(1.5, 0.05, 0.3, TRIM, [0, 1.44, -1.72])); // wide thin wing blade, floating high above the roof
  P.push(box(1.5, 0.03, 0.05, TRIM, [0, 1.45, -1.59])); // blade leading lip
  for (const s of S) P.push(box(0.03, 0.13, 0.3, TRIM, [s * 0.74, 1.43, -1.72])); // end plates

  // -- Front fascia: big teardrop swept headlamps in the front wings ----------
  for (const s of S) {
    P.push(box(0.42, 0.14, 0.4, LAMP, [s * 0.58, 0.67, 2.09], [HOOD_SLOPE, 0, 0])); // teardrop headlamp lens on the corner
    P.push(box(0.34, 0.05, 0.34, "#8fa5b2", [s * 0.58, 0.72, 2.14], [HOOD_SLOPE, 0, 0])); // projector band on the lens
    P.push(box(0.44, 0.05, 0.4, TRIM, [s * 0.58, 0.58, 2.09], [HOOD_SLOPE, 0, 0])); // slim dark bezel under the lamp
  }
  P.push(box(0.7, 0.13, 0.05, TRIM, [0, 0.42, 2.3])); // small lower grille intake
  for (let i = -2; i <= 2; i++) P.push(box(0.02, 0.09, 0.02, "#33373d", [i * 0.13, 0.42, 2.32])); // intake slats
  for (const s of S) P.push(cyl(0.055, 0.055, 0.04, 14, LAMP, [s * 0.5, 0.37, 2.31], [Math.PI / 2, 0, 0])); // round fog lamps
  P.push(cyl(0.05, 0.05, 0.02, 16, CHROME, [0, 0.64, 2.325], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.44, 0.1, 0.02, PLATE, [0, 0.4, 2.325])); // front plate

  // -- Rear fascia: wide wrap tail lamps, rounded diffuser, dual exhausts ------
  P.push(box(1.4, 0.02, 0.02, TAIL_RED, [0, 0.71, -2.2])); // high-level brake strip
  for (const s of S) {
    P.push(box(0.5, 0.15, 0.06, TAIL_RED, [s * 0.44, 0.6, -2.19])); // wide tail lamp bar
    P.push(box(0.06, 0.13, 0.14, TAIL_RED, [s * 0.85, 0.6, -2.12])); // corner wrap
    P.push(box(0.34, 0.07, 0.04, REDDARK, [s * 0.44, 0.6, -2.22])); // inner reflector band
  }
  P.push(box(0.5, 0.12, 0.04, TRIM, [0, 0.6, -2.2])); // black centre panel between the lamps
  P.push(box(0.44, 0.1, 0.02, PLATE, [0, 0.44, -2.2])); // rear plate
  P.push(box(1.5, 0.15, 0.12, CLAD, [0, 0.3, -2.12])); // rounded rear diffuser valance
  for (const s of S) for (let i = 0; i < 3; i++) {
    P.push(box(0.05, 0.08, 0.02, TRIM, [s * (0.2 + i * 0.14), 0.28, -2.18])); // diffuser fins
  }
  for (const dx of [-0.16, 0.16]) {
    P.push(cyl(0.07, 0.07, 0.1, 14, EXHAUST, [dx, 0.3, -2.18], [Math.PI / 2, 0, 0])); // dual round exhausts
  }

  // -- Furniture: mirrors, door handles, wiper --------------------------------
  for (const s of S) {
    P.push(box(0.09, 0.03, 0.05, TRIM, [s * 0.86, 0.78, 0.62])); // mirror stalk
    P.push(paint(0.09, 0.09, 0.13, [s * 0.93, 0.8, 0.6])); // rounded mirror head
    P.push(box(0.012, 0.07, 0.1, GLASS, [s * 0.885, 0.8, 0.53])); // mirror glass, facing back
    P.push(box(0.02, 0.03, 0.14, CHROME, [s * 0.905, 0.66, -0.3])); // rounded door handle
    P.push(box(0.3, 0.012, 0.03, TRIM, [s * 0.28, 0.78, 0.62], [-SCREEN_RAKE, 0, 0])); // wiper on the screen
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const toyotaSupraMk4 = defineModel({
  id: "toyota-supra-mk4",
  name: "Toyota Supra Mk4",
  batchId: "cars",
  tags: ["car", "toyota", "supra", "coupe", "sports", "jdm", "classic"],
  summary: "A Toyota Supra Mk4 (A80) sports coupe at true scale, in iconic Supra orange.",
  description:
    "A Toyota Supra Mk4 (A80, ~1997) at real 1 unit = 1 metre scale (about " +
    "4.51 long, 1.81 wide, a low ~1.24 tall, 2.55 wheelbase). The silhouette " +
    "is one low continuous fastback: a long low bonnet flows up a hard-raked " +
    "curved windscreen to a short low roof, then that roofline sweeps in one " +
    "unbroken descending line — backlight sail, boot deck, tail lip — down to a " +
    "rounded tail, with the low muscular haunches flaring wider than the front " +
    "well below the beltline. The defining cue is the tall basket-handle rear " +
    "wing — a wide flat black blade on two tall uprights, floating high above " +
    "the boot deck with an open gap beneath. The front is a smooth rounded " +
    "body-colour nose with big teardrop headlamps swept into the front wings, a " +
    "small lower intake and round fog lamps; the rear has wide wrap tail lamps, " +
    "a rounded diffuser and dual round exhausts, on bright five-spoke alloys. " +
    "Body panels carry the 'paint' recolour slot (iconic Supra orange); " +
    "`drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.4,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
